import React from 'react';
import { Card, Typography, Divider, Space, Button } from 'antd';
import { ArrowLeftOutlined, DownloadOutlined } from '@ant-design/icons';
import { useNavigate } from 'react-router-dom';
import { useI18n } from '../i18n';

const { Title, Paragraph, Text, Link } = Typography;

const PrivacyPolicyPage: React.FC = () => {
  const navigate = useNavigate();
  const { t } = useI18n();

  const handleDownloadPDF = () => {
    // 这里可以实现PDF下载功能
    console.log('Downloading privacy policy PDF...');
  };

  return (
    <div style={{ 
      minHeight: '100vh', 
      backgroundColor: '#f5f5f5', 
      padding: '24px 0' 
    }}>
      <div style={{ 
        maxWidth: '1200px', 
        margin: '0 auto', 
        padding: '0 16px' 
      }}>
        {/* 返回按钮 */}
        <Space style={{ marginBottom: '24px' }}>
          <Button 
            icon={<ArrowLeftOutlined />} 
            onClick={() => navigate(-1)}
          >
            {t('privacyPolicy.back')}
          </Button>
          <Button 
            type="primary" 
            icon={<DownloadOutlined />}
            onClick={handleDownloadPDF}
          >
            {t('privacyPolicy.downloadPDF')}
          </Button>
        </Space>

        <Card>
          <div style={{ maxWidth: '800px', margin: '0 auto', padding: '24px' }}>
            <Title level={1} style={{ textAlign: 'center', marginBottom: '32px' }}>
              {t('privacyPolicy.title')}
            </Title>
            
            <Paragraph style={{ textAlign: 'center', color: '#666', marginBottom: '32px' }}>
              {t('privacyPolicy.lastUpdated')}
            </Paragraph>

            <Divider />

            {/* 概述 */}
            <section style={{ marginBottom: '32px' }}>
              <Title level={2}>{t('privacyPolicy.sections.overview.title')}</Title>
              <Paragraph>
                {t('privacyPolicy.sections.overview.content')}
              </Paragraph>
            </section>

            {/* 信息收集 */}
            <section style={{ marginBottom: '32px' }}>
              <Title level={2}>{t('privacyPolicy.sections.informationCollection.title')}</Title>
              
              <Title level={3}>{t('privacyPolicy.sections.informationCollection.directlyProvided.title')}</Title>
              <ul>
                {t('privacyPolicy.sections.informationCollection.directlyProvided.items').map((item: string, index: number) => (
                  <li key={index}><strong>{item.split('：')[0]}：</strong>{item.split('：')[1]}</li>
                ))}
              </ul>

              <Title level={3}>{t('privacyPolicy.sections.informationCollection.automaticallyCollected.title')}</Title>
              <ul>
                {t('privacyPolicy.sections.informationCollection.automaticallyCollected.items').map((item: string, index: number) => (
                  <li key={index}><strong>{item.split('：')[0]}：</strong>{item.split('：')[1]}</li>
                ))}
              </ul>
            </section>

            {/* 信息使用 */}
            <section style={{ marginBottom: '32px' }}>
              <Title level={2}>{t('privacyPolicy.sections.informationUsage.title')}</Title>
              <Paragraph>{t('privacyPolicy.sections.informationUsage.content')}</Paragraph>
              <ul>
                {t('privacyPolicy.sections.informationUsage.purposes').map((purpose: string, index: number) => (
                  <li key={index}>{purpose}</li>
                ))}
              </ul>
            </section>

            {/* 法律依据 */}
            <section style={{ marginBottom: '32px' }}>
              <Title level={2}>{t('privacyPolicy.sections.legalBasis.title')}</Title>
              <ul>
                {t('privacyPolicy.sections.legalBasis.items').map((item: string, index: number) => (
                  <li key={index}><strong>{item.split('：')[0]}：</strong>{item.split('：')[1]}</li>
                ))}
              </ul>
            </section>

            {/* 信息共享 */}
            <section style={{ marginBottom: '32px' }}>
              <Title level={2}>{t('privacyPolicy.sections.informationSharing.title')}</Title>
              <Paragraph>{t('privacyPolicy.sections.informationSharing.content')}</Paragraph>
              <ul>
                {t('privacyPolicy.sections.informationSharing.scenarios').map((scenario: string, index: number) => (
                  <li key={index}><strong>{scenario.split('：')[0]}：</strong>{scenario.split('：')[1]}</li>
                ))}
              </ul>
            </section>

            {/* 数据安全 */}
            <section style={{ marginBottom: '32px' }}>
              <Title level={2}>{t('privacyPolicy.sections.dataSecurity.title')}</Title>
              <Paragraph>
                {t('privacyPolicy.sections.dataSecurity.content')}
              </Paragraph>
              <ul>
                {t('privacyPolicy.sections.dataSecurity.measures').map((measure: string, index: number) => (
                  <li key={index}>{measure}</li>
                ))}
              </ul>
            </section>

            {/* 数据保留 */}
            <section style={{ marginBottom: '32px' }}>
              <Title level={2}>{t('privacyPolicy.sections.dataRetention.title')}</Title>
              <Paragraph>
                {t('privacyPolicy.sections.dataRetention.content')}
              </Paragraph>
              <ul>
                {t('privacyPolicy.sections.dataRetention.periods').map((period: string, index: number) => (
                  <li key={index}><strong>{period.split('：')[0]}：</strong>{period.split('：')[1]}</li>
                ))}
              </ul>
            </section>

            {/* 您的权利 */}
            <section style={{ marginBottom: '32px' }}>
              <Title level={2}>{t('privacyPolicy.sections.yourRights.title')}</Title>
              
              <Title level={3}>{t('privacyPolicy.sections.yourRights.gdpr.title')}</Title>
              <ul>
                {t('privacyPolicy.sections.yourRights.gdpr.rights').map((right: string, index: number) => (
                  <li key={index}><strong>{right.split('：')[0]}：</strong>{right.split('：')[1]}</li>
                ))}
              </ul>

              <Title level={3}>{t('privacyPolicy.sections.yourRights.ccpa.title')}</Title>
              <ul>
                {t('privacyPolicy.sections.yourRights.ccpa.rights').map((right: string, index: number) => (
                  <li key={index}><strong>{right.split('：')[0]}：</strong>{right.split('：')[1]}</li>
                ))}
              </ul>
            </section>

            {/* Cookie政策 */}
            <section style={{ marginBottom: '32px' }}>
              <Title level={2}>{t('privacyPolicy.sections.cookies.title')}</Title>
              <Paragraph>
                {t('privacyPolicy.sections.cookies.content')}
              </Paragraph>
              <ul>
                {t('privacyPolicy.sections.cookies.types').map((type: string, index: number) => (
                  <li key={index}><strong>{type.split('：')[0]}：</strong>{type.split('：')[1]}</li>
                ))}
              </ul>
            </section>

            {/* 国际传输 */}
            <section style={{ marginBottom: '32px' }}>
              <Title level={2}>{t('privacyPolicy.sections.internationalTransfer.title')}</Title>
              <Paragraph>
                {t('privacyPolicy.sections.internationalTransfer.content')}
              </Paragraph>
            </section>

            {/* 儿童隐私 */}
            <section style={{ marginBottom: '32px' }}>
              <Title level={2}>{t('privacyPolicy.sections.childrenPrivacy.title')}</Title>
              <Paragraph>
                {t('privacyPolicy.sections.childrenPrivacy.content')}
              </Paragraph>
            </section>

            {/* 政策更新 */}
            <section style={{ marginBottom: '32px' }}>
              <Title level={2}>{t('privacyPolicy.sections.policyUpdates.title')}</Title>
              <Paragraph>
                {t('privacyPolicy.sections.policyUpdates.content')}
              </Paragraph>
            </section>

            {/* 联系我们 */}
            <section style={{ marginBottom: '32px' }}>
              <Title level={2}>{t('privacyPolicy.sections.contactUs.title')}</Title>
              <Paragraph>
                {t('privacyPolicy.sections.contactUs.content')}
              </Paragraph>
              <ul>
                {t('privacyPolicy.sections.contactUs.contactInfo').map((info: string, index: number) => (
                  <li key={index}><strong>{info.split('：')[0]}：</strong>{info.split('：')[1]}</li>
                ))}
              </ul>
              
              <Paragraph>
                <strong>{t('privacyPolicy.sections.contactUs.dpo')}</strong>
              </Paragraph>
            </section>

            {/* 监管机构 */}
            <section style={{ marginBottom: '32px' }}>
              <Title level={2}>{t('privacyPolicy.sections.regulatoryComplaints.title')}</Title>
              <Paragraph>
                {t('privacyPolicy.sections.regulatoryComplaints.content')}
              </Paragraph>
              <ul>
                {t('privacyPolicy.sections.regulatoryComplaints.authorities').map((authority: string, index: number) => (
                  <li key={index}><strong>{authority.split('：')[0]}：</strong>{authority.split('：')[1]}</li>
                ))}
              </ul>
            </section>

            <Divider />

            <div style={{ 
              textAlign: 'center', 
              padding: '24px', 
              backgroundColor: '#f8f9fa', 
              borderRadius: '8px' 
            }}>
              <Text type="secondary">
                {t('privacyPolicy.sections.footer.content')}
              </Text>
            </div>
          </div>
        </Card>
      </div>
    </div>
  );
};

export default PrivacyPolicyPage;
